/**
 * 🎨 智能气泡管理器
 *
 * 功能特性：
 * - 科技感毛玻璃蓝色半透明气泡
 * - 智能避障定位算法
 * - 内容自适应布局
 * - 设备关联和状态同步
 */

import * as THREE from 'three';

// 气泡配置接口
export interface BubbleConfig {
  /** 气泡唯一标识 */
  id: string;
  /** 关联的3D对象 */
  target: THREE.Object3D;
  /** 目标世界坐标位置 */
  targetPosition?: THREE.Vector3;
  /** 气泡标题 */
  title: string;
  /** 气泡内容 */
  content: BubbleContent;
  /** 是否启用自动定位 */
  autoPosition?: boolean;
  /** 手动指定位置偏移 */
  offset?: { x: number; y: number };
  /** 气泡类型样式 */
  type?: 'info' | 'warning' | 'error' | 'success';
  /** 是否显示连接线（已废弃，不再使用） */
  showConnector?: boolean;
  /** 最大宽度 */
  maxWidth?: number;
  /** 气泡位置偏好 */
  positionPreference?: 'bottom' | 'top' | 'auto';
  /** 气泡关闭回调 */
  onClose?: (id: string) => void;
}

// 气泡内容接口
export interface BubbleContent {
  /** 设备状态 */
  status?:
    | 'online'
    | 'offline'
    | 'warning'
    | 'maintenance'
    | 'error'
    | 'moving';
  /** 键值对数据 */
  data?: Record<string, any>;
  /** 自定义HTML内容 */
  html?: string;
  /** React组件内容 */
  component?: any;
  /** 操作按钮 */
  actions?: BubbleAction[];
  /** HLS视频流URL */
  videoUrl?: string;
  /** 视频类型 */
  videoType?: 'hls' | 'rtmp' | 'webrtc';
}

// 气泡操作按钮
export interface BubbleAction {
  label: string;
  icon?: string;
  onClick: () => void;
  type?: 'primary' | 'secondary' | 'danger';
}

// 气泡位置信息
export interface BubblePosition {
  x: number;
  y: number;
  anchor: 'top' | 'bottom' | 'left' | 'right';
  offset: { x: number; y: number };
}

// 气泡实例接口
export interface BubbleInstance {
  config: BubbleConfig;
  element: HTMLElement;
  position: BubblePosition;
  visible: boolean;
  connector?: HTMLElement;
}

/**
 * 智能气泡管理器
 */
export class BubbleManager {
  private container: HTMLElement;
  private camera: THREE.Camera;
  private bubbles: Map<string, BubbleInstance> = new Map();

  // 样式配置
  private readonly BUBBLE_STYLES = {
    background: 'rgba(0, 123, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(0, 123, 255, 0.3)',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 123, 255, 0.1)',
    color: '#ffffff',
    fontSize: '14px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    padding: '16px',
    maxWidth: '300px',
    minWidth: '200px',
    zIndex: '10000',
  };

  constructor(camera: THREE.Camera, container: HTMLElement) {
    this.camera = camera;
    this.container = container;

    this.initializeEventListeners();
    this.createBubbleContainer();
  }

  /**
   * 初始化事件监听器
   */
  private initializeEventListeners(): void {
    // 窗口大小变化时重新计算位置
    window.addEventListener('resize', () => {
      this.updateAllBubblePositions();
    });

    // 相机移动时更新位置
    this.container.addEventListener('mousemove', () => {
      requestAnimationFrame(() => {
        this.updateAllBubblePositions();
      });
    });
  }

  /**
   * 创建气泡容器
   */
  private createBubbleContainer(): void {
    let bubbleContainer = document.getElementById('v2x-bubble-container');
    if (!bubbleContainer) {
      bubbleContainer = document.createElement('div');
      bubbleContainer.id = 'v2x-bubble-container';
      bubbleContainer.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 9999;
      `;
      this.container.appendChild(bubbleContainer);
    }
  }

  /**
   * 添加气泡
   */
  addBubble(config: BubbleConfig): string {
    if (this.bubbles.has(config.id)) {
      console.warn(`气泡 ${config.id} 已存在，将被替换`);
      this.removeBubble(config.id);
    }

    const element = this.createBubbleElement(config);
    const position = this.calculateOptimalPosition(config);

    const instance: BubbleInstance = {
      config,
      element,
      position,
      visible: false,
    };

    this.bubbles.set(config.id, instance);
    this.updateBubblePosition(instance);

    return config.id;
  }

  /**
   * 移除气泡
   */
  removeBubble(id: string): boolean {
    const instance = this.bubbles.get(id);
    if (!instance) return false;

    instance.element.remove();
    this.bubbles.delete(id);
    return true;
  }

  /**
   * 显示气泡
   */
  showBubble(id: string): boolean {
    const instance = this.bubbles.get(id);
    if (!instance) return false;

    instance.visible = true;
    instance.element.style.display = 'block';
    instance.element.style.opacity = '0';

    // 动画显示
    requestAnimationFrame(() => {
      instance.element.style.transition =
        'opacity 0.3s ease, transform 0.3s ease';
      instance.element.style.opacity = '1';
      instance.element.style.transform = 'translateY(0) scale(1)';
    });

    this.updateBubblePosition(instance);
    return true;
  }

  /**
   * 隐藏气泡
   */
  hideBubble(id: string): boolean {
    const instance = this.bubbles.get(id);
    if (!instance) return false;

    instance.visible = false;
    instance.element.style.transition =
      'opacity 0.3s ease, transform 0.3s ease';
    instance.element.style.opacity = '0';
    instance.element.style.transform = 'translateY(-10px) scale(0.95)';

    setTimeout(() => {
      if (!instance.visible) {
        instance.element.style.display = 'none';
      }
    }, 300);

    return true;
  }

  /**
   * 更新气泡内容
   */
  updateBubbleContent(id: string, content: Partial<BubbleContent>): boolean {
    const instance = this.bubbles.get(id);
    if (!instance) return false;

    instance.config.content = { ...instance.config.content, ...content };
    const contentElement = instance.element.querySelector('.bubble-content');
    if (contentElement) {
      contentElement.innerHTML = this.generateContentHTML(
        instance.config.content
      );
    }

    return true;
  }

  /**
   * 创建气泡元素
   */
  private createBubbleElement(config: BubbleConfig): HTMLElement {
    const bubble = document.createElement('div');
    bubble.className = `v2x-bubble bubble-${config.type || 'info'}`;
    bubble.style.cssText = `
      position: absolute;
      background: ${this.BUBBLE_STYLES.background};
      backdrop-filter: ${this.BUBBLE_STYLES.backdropFilter};
      -webkit-backdrop-filter: ${this.BUBBLE_STYLES.backdropFilter};
      border: ${this.BUBBLE_STYLES.border};
      border-radius: ${this.BUBBLE_STYLES.borderRadius};
      box-shadow: ${this.BUBBLE_STYLES.boxShadow};
      color: ${this.BUBBLE_STYLES.color};
      font-size: ${this.BUBBLE_STYLES.fontSize};
      font-family: ${this.BUBBLE_STYLES.fontFamily};
      padding: ${this.BUBBLE_STYLES.padding};
      max-width: ${config.maxWidth ? config.maxWidth + 'px' : this.BUBBLE_STYLES.maxWidth};
      min-width: ${this.BUBBLE_STYLES.minWidth};
      z-index: ${this.BUBBLE_STYLES.zIndex};
      pointer-events: auto;
      display: none;
      opacity: 0;
      transform: translateY(-10px) scale(0.95);
      transition: opacity 0.3s ease, transform 0.3s ease;
    `;

    // 添加类型特定样式
    if (config.type === 'warning') {
      bubble.style.borderColor = 'rgba(255, 193, 7, 0.5)';
      bubble.style.background = 'rgba(255, 193, 7, 0.15)';
      bubble.style.boxShadow = '0 8px 32px rgba(255, 193, 7, 0.1)';
    } else if (config.type === 'error') {
      bubble.style.borderColor = 'rgba(220, 53, 69, 0.5)';
      bubble.style.background = 'rgba(220, 53, 69, 0.15)';
      bubble.style.boxShadow = '0 8px 32px rgba(220, 53, 69, 0.1)';
    } else if (config.type === 'success') {
      bubble.style.borderColor = 'rgba(40, 167, 69, 0.5)';
      bubble.style.background = 'rgba(40, 167, 69, 0.15)';
      bubble.style.boxShadow = '0 8px 32px rgba(40, 167, 69, 0.1)';
    }

    bubble.innerHTML = `
      <div class="bubble-header" style="
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;
        font-weight: 600;
      ">
        <span class="bubble-title">${config.title}</span>
        <span class="bubble-close" style="
          cursor: pointer;
          opacity: 0.7;
          font-size: 16px;
          line-height: 1;
          padding: 4px;
          border-radius: 4px;
          transition: opacity 0.2s;
        " data-bubble-id="${config.id}" data-action="close">✕</span>
      </div>
      <div class="bubble-content" style="line-height: 1.5;">
        ${this.generateContentHTML(config.content)}
      </div>
    `;

    // 添加悬停效果
    bubble.addEventListener('mouseenter', () => {
      bubble.style.transform = 'translateY(0) scale(1.02)';
      bubble.style.boxShadow = bubble.style.boxShadow.replace('0.1)', '0.2)');
    });

    bubble.addEventListener('mouseleave', () => {
      bubble.style.transform = 'translateY(0) scale(1)';
      bubble.style.boxShadow = bubble.style.boxShadow.replace('0.2)', '0.1)');
    });

    // 添加关闭按钮事件监听器
    const closeButton = bubble.querySelector('[data-action="close"]');
    if (closeButton) {
      closeButton.addEventListener('click', e => {
        e.stopPropagation();
        this.hideBubble(config.id);
        // 触发关闭回调
        if (config.onClose) {
          config.onClose(config.id);
        }
      });
    }

    const container = document.getElementById('v2x-bubble-container');
    if (container) {
      container.appendChild(bubble);
    }

    // 初始化视频播放器
    if (config.content.videoUrl) {
      this.initializeHLS();
      // 延迟执行，确保DOM元素已渲染
      setTimeout(() => {
        this.setupVideoPlayer(bubble);
      }, 100);
    }

    return bubble;
  }

  /**
   * 生成内容HTML
   */
  private generateContentHTML(content: BubbleContent): string {
    let html = '';

    // 状态显示
    if (content.status) {
      const statusColors = {
        online: '#28a745',
        offline: '#dc3545',
        warning: '#ffc107',
        maintenance: '#6c757d',
        error: '#dc3545',
        moving: '#17a2b8',
      };
      const statusLabels = {
        online: '在线',
        offline: '离线',
        warning: '警告',
        maintenance: '维护中',
        error: '错误',
        moving: '移动中',
      };

      html += `
        <div style="
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          padding: 8px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 6px;
        ">
          <div style="
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: ${statusColors[content.status]};
          "></div>
          <span style="font-weight: 500;">${statusLabels[content.status]}</span>
        </div>
      `;
    }

    // 视频播放器
    if (content.videoUrl) {
      html += this.createVideoPlayer(content.videoUrl, content.videoType);
    }

    // 数据显示
    if (content.data) {
      html += '<div class="bubble-data">';
      Object.entries(content.data).forEach(([key, value]) => {
        html += `
          <div style="
            display: flex;
            justify-content: space-between;
            margin-bottom: 6px;
            padding: 4px 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          ">
            <span style="opacity: 0.8;">${key}:</span>
            <span style="font-weight: 500;">${value}</span>
          </div>
        `;
      });
      html += '</div>';
    }

    // 自定义HTML内容
    if (content.html) {
      html += content.html;
    }

    // 操作按钮
    if (content.actions && content.actions.length > 0) {
      html +=
        '<div class="bubble-actions" style="margin-top: 12px; display: flex; gap: 8px;">';
      content.actions.forEach(action => {
        const buttonStyles = {
          primary: 'background: rgba(0, 123, 255, 0.8); color: white;',
          secondary: 'background: rgba(255, 255, 255, 0.2); color: white;',
          danger: 'background: rgba(220, 53, 69, 0.8); color: white;',
        };

        html += `
          <button style="
            ${buttonStyles[action.type || 'secondary']}
            border: none;
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 12px;
            cursor: pointer;
            transition: all 0.2s;
          " onclick="${action.onClick}">
            ${action.icon ? action.icon + ' ' : ''}${action.label}
          </button>
        `;
      });
      html += '</div>';
    }

    return html;
  }

  /**
   * 计算最佳位置（优先显示在下方）
   */
  private calculateOptimalPosition(config: BubbleConfig): BubblePosition {
    // 如果有手动偏移，直接使用
    if (config.offset) {
      return {
        x: config.offset.x,
        y: config.offset.y,
        anchor: 'top',
        offset: { x: 0, y: 0 },
      };
    }

    // 获取目标在屏幕上的投影位置
    const targetPosition = config.targetPosition || config.target.position;
    const screenPosition = this.worldToScreen(targetPosition);

    // 获取容器尺寸
    const containerRect = this.container.getBoundingClientRect();

    // 计算最佳锚点和偏移
    const bubbleWidth = config.maxWidth || 320; // 估算气泡宽度
    const bubbleHeight = config.content.videoUrl ? 300 : 200; // 有视频时增加高度
    const margin = 20; // 边距

    let anchor: 'top' | 'bottom' | 'left' | 'right' = 'top';
    let x = screenPosition.x;
    let y = screenPosition.y;

    // 位置偏好逻辑（优先下方）
    const preference = config.positionPreference || 'bottom';

    if (preference === 'bottom') {
      // 优先显示在下方
      if (screenPosition.y + bubbleHeight + margin < containerRect.height) {
        anchor = 'top';
        y = screenPosition.y + margin;
      } else {
        // 下方空间不足，显示在上方
        anchor = 'bottom';
        y = screenPosition.y - bubbleHeight - margin;
      }
    } else if (preference === 'top') {
      // 优先显示在上方
      if (screenPosition.y - bubbleHeight - margin > 0) {
        anchor = 'bottom';
        y = screenPosition.y - bubbleHeight - margin;
      } else {
        // 上方空间不足，显示在下方
        anchor = 'top';
        y = screenPosition.y + margin;
      }
    } else {
      // 自动选择（旧逻辑）
      if (screenPosition.y > containerRect.height / 2) {
        anchor = 'bottom';
        y = screenPosition.y - bubbleHeight - margin;
      } else {
        anchor = 'top';
        y = screenPosition.y + margin;
      }
    }

    // 水平位置调整（居中对齐设备）
    x = screenPosition.x - bubbleWidth / 2;

    // 确保不超出容器边界
    if (x + bubbleWidth > containerRect.width - margin) {
      x = containerRect.width - bubbleWidth - margin;
    }
    if (x < margin) {
      x = margin;
    }

    // 确保垂直位置不超出边界
    if (y < margin) {
      y = margin;
    }
    if (y + bubbleHeight > containerRect.height - margin) {
      y = containerRect.height - bubbleHeight - margin;
    }

    return {
      x,
      y,
      anchor,
      offset: { x: 0, y: 0 },
    };
  }

  /**
   * 世界坐标转屏幕坐标
   */
  private worldToScreen(worldPosition: THREE.Vector3): {
    x: number;
    y: number;
  } {
    const vector = worldPosition.clone();
    vector.project(this.camera);

    const containerRect = this.container.getBoundingClientRect();
    const x = (vector.x * 0.5 + 0.5) * containerRect.width;
    const y = (vector.y * -0.5 + 0.5) * containerRect.height;

    return { x, y };
  }

  /**
   * 更新气泡位置
   */
  private updateBubblePosition(instance: BubbleInstance): void {
    if (!instance.visible) return;

    const position = this.calculateOptimalPosition(instance.config);
    instance.position = position;

    instance.element.style.left = position.x + 'px';
    instance.element.style.top = position.y + 'px';
  }

  /**
   * 更新所有气泡位置
   */
  private updateAllBubblePositions(): void {
    this.bubbles.forEach(instance => {
      if (instance.visible) {
        this.updateBubblePosition(instance);
      }
    });
  }

  /**
   * 初始化HLS.js用于视频播放
   */
  private initializeHLS(): void {
    // 动态加载HLS.js
    if (typeof window !== 'undefined' && !(window as any).Hls) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
      script.onload = () => {
        console.log('HLS.js 加载完成');
      };
      document.head.appendChild(script);
    }
  }

  /**
   * 创建视频播放器元素
   */
  private createVideoPlayer(
    videoUrl: string,
    videoType: string = 'hls'
  ): string {
    const videoId = `video-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return `
      <div class="bubble-video-container" style="
        margin: 8px 0;
        border-radius: 8px;
        overflow: hidden;
        background: rgba(0, 0, 0, 0.8);
      ">
        <video 
          id="${videoId}" 
          controls 
          muted 
          preload="metadata"
          style="
            width: 100%;
            height: 180px;
            object-fit: cover;
            display: block;
          "
          data-video-url="${videoUrl}"
          data-video-type="${videoType}"
        >
          <source src="${videoUrl}" type="application/x-mpegURL">
          您的浏览器不支持视频播放
        </video>
        <div class="video-controls" style="
          padding: 8px;
          background: rgba(0, 0, 0, 0.9);
          font-size: 12px;
          color: #fff;
        ">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span>🎥 ${videoType.toUpperCase()} 视频流</span>
            <button onclick="this.closest('.bubble-video-container').querySelector('video').load()" style="
              background: rgba(255, 255, 255, 0.2);
              border: none;
              color: #fff;
              padding: 4px 8px;
              border-radius: 4px;
              cursor: pointer;
              font-size: 11px;
            ">重连</button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * 设置视频播放（支持HLS）
   */
  private setupVideoPlayer(container: HTMLElement): void {
    const videos = container.querySelectorAll('video[data-video-url]');

    videos.forEach(element => {
      const video = element as HTMLVideoElement;
      const videoUrl = video.dataset.videoUrl;
      const videoType = video.dataset.videoType || 'hls';

      if (videoType === 'hls' && videoUrl) {
        // 检查是否支持原生HLS
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = videoUrl;
        }
        // 使用HLS.js
        else if ((window as any).Hls && (window as any).Hls.isSupported()) {
          const HlsClass = (window as any).Hls;
          const hls = new HlsClass({
            enableWorker: true,
            lowLatencyMode: true,
            backBufferLength: 90,
          });

          hls.loadSource(videoUrl);
          hls.attachMedia(video);

          hls.on(HlsClass.Events.MANIFEST_PARSED, () => {
            console.log('HLS 清单解析完成');
          });

          hls.on(HlsClass.Events.ERROR, (_event: any, data: any) => {
            console.warn('HLS 错误:', data);
            if (data.fatal) {
              switch (data.type) {
                case HlsClass.ErrorTypes.NETWORK_ERROR:
                  console.log('网络错误，尝试恢复...');
                  hls.startLoad();
                  break;
                case HlsClass.ErrorTypes.MEDIA_ERROR:
                  console.log('媒体错误，尝试恢复...');
                  hls.recoverMediaError();
                  break;
                default:
                  console.log('致命错误，销毁HLS实例');
                  hls.destroy();
                  break;
              }
            }
          });

          // 保存hls实例以便后续清理
          (video as any).hlsInstance = hls;
        } else {
          console.warn('不支持HLS播放');
          video.style.display = 'none';
        }
      }
    });
  }

  /**
   * 启用/禁用气泡管理器
   */
  setEnabled(enabled: boolean): void {
    this.bubbles.forEach(instance => {
      instance.element.style.display =
        enabled && instance.visible ? 'block' : 'none';
    });
  }

  /**
   * 获取所有气泡信息
   */
  getAllBubbles(): Array<{
    id: string;
    config: BubbleConfig;
    visible: boolean;
  }> {
    return Array.from(this.bubbles.entries()).map(([id, instance]) => ({
      id,
      config: instance.config,
      visible: instance.visible,
    }));
  }

  /**
   * 清理所有气泡
   */
  dispose(): void {
    this.bubbles.forEach(instance => {
      // 清理视频播放器实例
      const videos = instance.element.querySelectorAll('video[data-video-url]');
      videos.forEach((video: any) => {
        if (video.hlsInstance) {
          video.hlsInstance.destroy();
        }
      });

      instance.element.remove();
    });

    this.bubbles.clear();

    const container = document.getElementById('v2x-bubble-container');
    if (container) {
      container.remove();
    }
  }
}

// 导出工厂函数
export function createBubbleManager(
  camera: THREE.Camera,
  container: HTMLElement
): BubbleManager {
  return new BubbleManager(camera, container);
}
